import { useEffect, useState } from "react";

import { BookmarkButton as AngelouBookmarkButton } from "@newsela/angelou";
import PropTypes from "prop-types";

import {
  actionNames,
  actionPrefixes,
  objectTypes,
  withTrackEvent,
} from "@newsela/snitch";
import globalToasterRef from "@/base/globalToasterRef";
import getUserProperties from "@/users/getUserProperties";
import useBookmarkContentContext from "@/users/useBookmarkContent/useBookmarkContent";
import useBookmarkContentCreate from "@/users/useBookmarkContentCreate/useBookmarkContentCreate";
import useBookmarkContentDelete from "@/users/useBookmarkContentDelete/useBookmarkContentDelete";
import useUser from "@/users/useUser";
import withHoverStateLabel from "@/view/withHoverStateLabel";
import { iconCss } from "./BookmarkButton.style";
import {
  BOOKMARK_ERROR_TOAST_PROPS,
  BOOKMARK_SUCCESS_TOAST_PROPS,
  UNBOOKMARK_ERROR_TOAST_PROPS,
  UNBOOKMARK_SUCCESS_TOAST_PROPS,
} from "./BookmarkButton.constants";

const propTypes = {
  /* Pass content_id derived from the V3 Content API  as a string */
  contentId: PropTypes.string,
  /* Pass content_id of the currently selected lexile level */
  currentLevelId: PropTypes.string,
  /* Set a custom color for the bookmark button icon  */
  hoverLabel: PropTypes.string,
  /* Set a custom label that appears when hovering over the bookmark button  */
  iconColor: PropTypes.string,
  /* Text that renders with the bookmark button icon */
  label: PropTypes.shape({
    active: PropTypes.string,
    inactive: PropTypes.string,
  }),
  /* Passed to the event properties for the button */
  streamProperties: PropTypes.object,
  /* Use __cssFor to customize Angelou's BookmarkButton */
  __cssFor: PropTypes.shape({
    root: PropTypes.object,
    Button: PropTypes.shape({
      root: PropTypes.object,
    }),
    Icon: PropTypes.shape({
      root: PropTypes.object,
    }),
    label: PropTypes.object,
  }),
};

const AngelouBookmarkButtonWithTrackEvent = withTrackEvent(
  withHoverStateLabel(AngelouBookmarkButton),
);

const BookmarkButton = ({
  contentId = "",
  currentLevelId,
  hoverLabel,
  iconColor,
  label,
  streamProperties,
  __cssFor,
}) => {
  const { data: user } = useUser();
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [createBookmark] = useBookmarkContentCreate();
  const [deleteBookmark] = useBookmarkContentDelete();

  /* The BookmarkContextProvider should be wrapped around the lowest common ancestor or multiple BookmarkButtons that appear on the same page i.e, on the page-level or row-level  */
  const { bookmarkData, setBookmarkData } = useBookmarkContentContext();
  const { isLiteUser, isStudent } = getUserProperties(user);

  const bookmarkDataForContentId = bookmarkData?.[contentId];

  const eventProperties = {
    objectType: objectTypes.content,
    actionPrefix: isBookmarked ? actionPrefixes.undo : actionPrefixes.initiate,
    actionName: actionNames.add,
    streamProperties: {
      ...streamProperties,
      content_type: "Article",
      description: `${isBookmarked ? "Remove" : "Save"} Content`,
      content_id: currentLevelId || contentId,
    },
  };

  useEffect(() => {
    setIsBookmarked(Boolean(bookmarkDataForContentId));
  }, [bookmarkData, contentId]);

  if (isLiteUser && isStudent) {
    return null;
  }

  const addBookmark = async () => {
    const { data, error } = await createBookmark({ content_id: contentId });

    if (error) {
      globalToasterRef.current.displayToast(BOOKMARK_ERROR_TOAST_PROPS);
    } else {
      setIsBookmarked(true);
      setBookmarkData({
        ...bookmarkData,
        [contentId]: { id: data.id },
      });
      globalToasterRef.current.displayToast(BOOKMARK_SUCCESS_TOAST_PROPS);
    }
  };

  const removeBookmark = async () => {
    const { error } = await deleteBookmark(bookmarkDataForContentId?.id);

    if (error) {
      globalToasterRef.current.displayToast(UNBOOKMARK_ERROR_TOAST_PROPS);
    } else {
      setIsBookmarked(false);
      delete bookmarkData[contentId];
      globalToasterRef.current.displayToast(UNBOOKMARK_SUCCESS_TOAST_PROPS);
    }
  };

  const handleClick = () => {
    isBookmarked ? removeBookmark() : addBookmark();
  };

  if (!user?.is_authenticated) {
    return <></>;
  }

  return (
    <AngelouBookmarkButtonWithTrackEvent
      __cssFor={{
        ...__cssFor,
        Icon: {
          root: [
            iconColor ? iconCss(iconColor, isBookmarked) : {},
            __cssFor?.Icon?.root,
          ],
        },
      }}
      active={isBookmarked}
      hoverLabel={hoverLabel}
      label={label}
      onClick={handleClick}
      dataProps={{
        "data-qa-selector": isBookmarked
          ? "remove-from-my-content"
          : "save-to-my-content",
      }}
      {...eventProperties}
    />
  );
};

BookmarkButton.propTypes = propTypes;
export default BookmarkButton;
